import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PowerOutlinedIcon from '@mui/icons-material/PowerOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useClerk, useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import { clerkEnabled } from '../auth/clerk';
import CircularProgress from '@mui/material/CircularProgress';
import useVoyagerStore from '../stores/useVoyagerStore';
import { openQuestions, useBuildsSummary } from '../components/Builds/buildsSummary';
import type { Features } from '../stores/slices/featuresSlice';
import useStudioStore from '../stores/useStudioStore';
import { isOpenThread } from '../stores/slices/threadsSlice';
import { getCurrentUser } from '../utils';
import type { ConversationThread } from '../types';
import { ThreadRow } from './ChatSidebar';
import { activeAt } from './recents';
import { PHONE_QUERY, startNewQuestion, useChatSidebar } from './chatSidebarState';
import { useSimpleSidebar, type SimplePlaceholder } from './simpleSidebarState';
import { ACTION_ITEMS_REFRESH_MS, useActionItems } from './actionItems';
import { openSearchPalette } from './searchPaletteState';
import CloseUndoToast from './CloseUndoToast';
import FinishedToastView from './FinishedToast';

/**
 * The simplified app's one sidebar (#1503), on every page: Search, Home, Action items, Knowledge, Agents ·
 * Build (Semantic layer, Query builder, Sources, Impact) · Recents with the
 * mock's markers · the user menu (Settings, Help, Switch workspace, Sign out). Objectives and Reviews are
 * parked (`parkedSurfaces.ts`): their routes still open by URL.
 */

export const SIMPLE_SIDEBAR_WIDTH = 256;
/** How many conversations Recents lists; the rest are in History. */
export const RECENTS_SHOWN = 30;

type View = ReturnType<typeof useVoyagerStore.getState>['currentView'];

/** Each destination: what opens it, and when it is the page on screen. */
export const SIMPLE_DESTINATIONS: ReadonlyArray<{
  id: string; label: string; group: 'main' | 'build'; icon: React.ReactNode;
  go: () => void; active: (view: View, placeholder: SimplePlaceholder | null, home: boolean) => boolean;
  /** Listed only while this holds (a destination behind a switch). */
  shown?: (features: Features) => boolean;
}> = [
  // the search palette (#1514): it opens over the page (which stays, a placeholder included), so it is never the page on screen
  { id: 'search', label: 'Search', group: 'main', icon: <SearchIcon />, go: openSearchPalette, active: () => false },
  { id: 'home', label: 'Home', group: 'main', icon: <HomeOutlinedIcon />, go: startNewQuestion, active: (v, p, home) => !p && v === 'landing' && home },
  { id: 'action-items', label: 'Action items', group: 'main', icon: <TaskAltOutlinedIcon />, go: () => useSimpleSidebar.getState().showPlaceholder('action-items'), active: (_v, p) => p === 'action-items' },
  { id: 'knowledge', label: 'Knowledge', group: 'main', icon: <MenuBookOutlinedIcon />, go: () => useVoyagerStore.getState().navigateToBrain(), active: (v, p) => !p && ['brain', 'definitions', 'metric-graph'].includes(v) },
  { id: 'agents', label: 'Agents', group: 'main', icon: <AutoAwesomeOutlinedIcon />, go: () => useSimpleSidebar.getState().showPlaceholder('agents'), active: (_v, p) => p === 'agents' },
  { id: 'semantic-layer', label: 'Semantic layer', group: 'build', icon: <LayersOutlinedIcon />, go: () => { useStudioStore.getState().setStudioHome('overview'); useVoyagerStore.getState().navigateToStudio(); }, active: (v, p) => !p && (v === 'studio' || v === 'setup') },
  // every build, its own link (#1624); with AGENT_BUILDER off there is no item
  { id: 'builds', label: 'Builds', group: 'build', icon: <ConstructionOutlinedIcon />, go: () => useVoyagerStore.getState().navigateToBuilds(null), active: (v, p) => !p && v === 'builds', shown: (f) => f.agent_builder },
  // the ETL builder's Brain (#2288): what it has worked out about tables, pipelines and code; with PIPELINES off there is no item
  { id: 'pipeline-brain', label: 'Brain', group: 'build', icon: <PsychologyOutlinedIcon />, go: () => useVoyagerStore.getState().navigateToPipelineBrain(null), active: (v, p) => !p && v === 'pipeline_brain', shown: (f) => f.pipelines },
  // the ETL builder (#2262): with PIPELINES off there is no item
  { id: 'pipelines', label: 'Pipelines', group: 'build', icon: <AccountTreeOutlinedIcon />, go: () => useVoyagerStore.getState().navigateToPipelines(), active: (v, p) => !p && v === 'pipelines', shown: (f) => f.pipelines },
  { id: 'query-builder', label: 'Query builder', group: 'build', icon: <TableChartOutlinedIcon />, go: () => useVoyagerStore.getState().openWorkspace(), active: (v, p) => !p && (v === 'workspace' || v === 'browse') },
  { id: 'sources', label: 'Sources', group: 'build', icon: <PowerOutlinedIcon />, go: () => useVoyagerStore.getState().navigateToConnections(), active: (v, p) => !p && (v === 'connections' || v === 'extension') },
  { id: 'impact', label: 'Impact', group: 'build', icon: <TrendingUpOutlinedIcon />, go: () => useSimpleSidebar.getState().showPlaceholder('impact'), active: (_v, p) => p === 'impact' },
];

/** Recents lists conversations only: review documents, agent chats and groups are parked (#1503). */
export function simpleRecents(threads: ConversationThread[]): ConversationThread[] {
  return threads
    .filter((t) => isOpenThread(t) && (!t.kind || t.kind === 'thread'))
    .sort((a, b) => activeAt(b) - activeAt(a))
    .slice(0, RECENTS_SHOWN);
}

const FOCUS = { '&:focus-visible': { outline: '2px solid var(--acc)', outlineOffset: -2 } } as const;
const navSx = (on: boolean) => ({
  appearance: 'none', border: 0, width: '100%', height: 32, display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25,
  borderRadius: '7px', font: 'inherit', fontSize: 13.5, textAlign: 'left', cursor: 'pointer',
  bgcolor: on ? 'var(--active)' : 'transparent', color: on ? 'var(--ink)' : 'var(--ink-2)', fontWeight: on ? 500 : 400,
  '& svg': { fontSize: 17, color: on ? 'var(--acc)' : 'inherit' },
  '&:hover': { bgcolor: on ? 'var(--active)' : 'var(--hover)', color: 'var(--ink)' }, ...FOCUS,
});
const lblSx = { fontSize: 11.5, fontWeight: 500, color: 'var(--ink-3)', px: 1.25, pb: 0.5 } as const;
const countSx = { ml: 'auto', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, color: 'var(--ink-2)', bgcolor: 'var(--sunk)', border: '1px solid var(--line)', borderRadius: '5px', px: 0.75 } as const;

/** Leaves a placeholder page and the phone drawer, then opens a destination. */
function go(open: () => void) {
  useSimpleSidebar.getState().showPlaceholder(null);
  useChatSidebar.getState().setDrawerOpen(false);
  open();
}

/** The user row (the mock's): the person and their workspace; the one menu offers Settings, Help, Switch workspace, Stroma support for platform owners, and Sign out. */
function UserMenu() {
  return clerkEnabled() ? <ClerkUserMenu /> : <UserMenuView name={getCurrentUser() || 'Local user'} />;
}

/** Initials of a name: the first letter of its first two words. */
function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w.slice(0, 1)).join('').toUpperCase();
}

/** Clerk's hooks need its provider, which is mounted only when Clerk is on. */
function ClerkUserMenu() {
  const { user } = useUser();
  const { organization, membership } = useOrganization();
  const { signOut } = useClerk();
  const name = user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress || '';
  const workspace = organization ? `${organization.name} · ${membership?.role === 'org:admin' ? 'Admin' : 'Member'}` : 'Personal';
  return <UserMenuView name={name} workspace={workspace} switcher onSignOut={() => void signOut({ redirectUrl: '/' })} />;
}

/** The switch submenu: the workspaces the person belongs to, every page of them read only while it is open. */
function WorkspaceMenu({ anchor, onClose, onPicked }: { anchor: HTMLElement; onClose: () => void; onPicked: () => void }) {
  const { setActive, userMemberships } = useOrganizationList({ userMemberships: { infinite: true } });
  const { organization } = useOrganization();
  const { hasNextPage, isFetching, fetchNext } = userMemberships;
  React.useEffect(() => { if (hasNextPage && !isFetching) fetchNext?.(); }, [hasNextPage, isFetching, fetchNext]);
  const pick = (id: string | null) => () => { onPicked(); if (setActive) void setActive({ organization: id, redirectUrl: '/' }); };
  return <Menu anchorEl={anchor} open onClose={onClose} variant="selectedMenu" anchorOrigin={{ vertical: 'top', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
    <MenuItem selected={!organization} onClick={pick(null)}>Personal</MenuItem>
    {(userMemberships.data || []).map((m) => <MenuItem key={m.organization.id} selected={organization?.id === m.organization.id} onClick={pick(m.organization.id)}>{m.organization.name}</MenuItem>)}
    {(userMemberships.isLoading || hasNextPage) && <MenuItem disabled>Loading…</MenuItem>}
  </Menu>;
}

function UserMenuView({ name, workspace, switcher, onSignOut }: { name: string; workspace?: string; switcher?: boolean; onSignOut?: () => void }) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [switchAnchor, setSwitchAnchor] = React.useState<HTMLElement | null>(null);
  const platformOwner = useVoyagerStore((s) => s.platformOwner);
  const settingsOn = useVoyagerStore((s) => s.currentView === 'agent_studio');
  const shut = () => { setSwitchAnchor(null); setAnchor(null); };
  const close = (then: () => void) => () => { shut(); go(then); };
  const icon = { fontSize: 17, mr: 1.25 };
  return <>
    <Box component="button" type="button" data-testid="simple-user-menu" aria-label={`Account: ${name}`} aria-haspopup="menu" aria-expanded={!!anchor}
      aria-current={settingsOn ? 'page' : undefined} onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
      sx={{ ...navSx(settingsOn), height: 'auto', py: 0.75, gap: 1.25, minWidth: 0 }}>
      <Box component="span" aria-hidden data-testid="simple-user-initials" sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'var(--ink)', color: 'var(--bg)', display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 600, flex: 'none' }}>
        {initialsOf(name)}
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', '& > span': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }}>
        <Box component="span" sx={{ color: 'var(--ink)' }}>{name}</Box>
        {workspace && <Box component="span" sx={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{workspace}</Box>}
      </Box>
    </Box>
    <Menu anchorEl={anchor} open={!!anchor} onClose={shut} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
      <MenuItem onClick={close(() => useVoyagerStore.getState().navigateToAgentStudio('appearance'))}><SettingsOutlinedIcon sx={icon} />Settings</MenuItem>
      {/* Help is a question to Stroma: its help answers come from the product's own help articles */}
      <MenuItem onClick={close(startNewQuestion)}><HelpOutlineIcon sx={icon} /><Box><Box>Help</Box><Box sx={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Ask Stroma how to do something</Box></Box></MenuItem>
      {switcher && <MenuItem aria-haspopup="menu" aria-expanded={!!switchAnchor} onClick={(e: React.MouseEvent<HTMLElement>) => setSwitchAnchor(e.currentTarget)}>
        <SwapHorizOutlinedIcon sx={icon} />Switch workspace<ChevronRightIcon sx={{ fontSize: 17, ml: 'auto', pl: 1, color: 'var(--ink-3)' }} />
      </MenuItem>}
      {platformOwner && <MenuItem onClick={close(() => useVoyagerStore.getState().navigateToAgentStudio('support'))}><SupportAgentOutlinedIcon sx={icon} />Stroma support</MenuItem>}
      {onSignOut && <MenuItem onClick={() => { shut(); onSignOut(); }}><LogoutOutlinedIcon sx={icon} />Sign out</MenuItem>}
    </Menu>
    {switcher && switchAnchor && <WorkspaceMenu anchor={switchAnchor} onClose={() => setSwitchAnchor(null)} onPicked={shut} />}
  </>;
}

/** Keeps Action items current while the sidebar is shown: on mount, on a timer, on focus and on a workspace's connections changing. */
function useActionItemsRefresh() {
  const generation = useVoyagerStore((s) => s.connectionsGeneration);
  React.useEffect(() => {
    const load = () => void useActionItems.getState().load({ background: true });
    const onFocus = () => void useActionItems.getState().load({ background: true, ifStale: true });
    void useActionItems.getState().load();
    const interval = window.setInterval(load, ACTION_ITEMS_REFRESH_MS);
    window.addEventListener('focus', onFocus);
    return () => { window.clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [generation]);
}

/**
 * Build › Builds (#1625 R8): a spinner while one of the viewer's own builds runs, and the count of questions waiting
 * on them (accent) or else how many run — from the shell's builds summary, the status read model's brief projection.
 */
function BuildsItem({ d, on, onGo }: { d: typeof SIMPLE_DESTINATIONS[number]; on: boolean; onGo: () => void }) {
  const data = useBuildsSummary((s) => s.data);
  const running = data ? data.running : 0;
  const questions = openQuestions(data);
  const said = questions ? `${questions} question${questions === 1 ? '' : 's'} for you` : running ? `${running} running` : null;
  return <Box component="button" type="button" data-testid={`simple-nav-${d.id}`} aria-current={on ? 'page' : undefined}
    aria-label={said ? `${d.label}, ${said}` : undefined} onClick={onGo} sx={navSx(on)}>
    {running ? <CircularProgress data-testid="builds-spinner" size={16} thickness={5} aria-hidden
      sx={{ color: 'var(--acc)', flexShrink: 0, mx: '1px', '@media (prefers-reduced-motion: reduce)': { animation: 'none', '& circle': { animation: 'none', strokeDasharray: '40px, 200px' } } }} />
      : d.icon}<span>{d.label}</span>
    {questions ? <Box component="span" data-testid="builds-count" data-hot="on" sx={{ ...countSx, color: 'var(--bg)', bgcolor: 'var(--acc)', borderColor: 'var(--acc)' }}>{questions > 99 ? '99+' : questions}</Box>
      : running ? <Box component="span" data-testid="builds-count" sx={{ ...countSx, bgcolor: 'transparent', color: 'var(--ink-3)', fontWeight: 500 }}>{running} running</Box> : null}
  </Box>;
}

/** The sidebar's body; the same on a wide screen and in the phone drawer. */
export default function SimpleSidebar({ inDrawer = false }: { inDrawer?: boolean }) {
  const view = useVoyagerStore((s) => s.currentView);
  const features = useVoyagerStore((s) => s.features);
  const home = useVoyagerStore((s) => !s.currentThreadId && s.landingAgentTurns.length === 0 && !s.objectiveSpaceId);
  const threads = useVoyagerStore((s) => s.savedThreads);
  // until the first list answers, an empty Recents is not yet known to be empty
  const threadsPending = useVoyagerStore((s) => !s.threadsLoaded && !s.threadsError);
  const placeholder = useSimpleSidebar((s) => s.placeholder);
  const phone = useMediaQuery(PHONE_QUERY);
  const recents = React.useMemo(() => simpleRecents(threads), [threads]);
  // the Action items page's own rows (#1509): the badge is always the number of rows it shows; Knowledge's count
  // comes from the same read, so each queue is read once per refresh
  const actionCount = useActionItems((s) => s.rows.length);
  const knowledgeCount = useActionItems((s) => s.knowledgeCount);
  useActionItemsRefresh();
  const count: Record<string, number> = { 'action-items': actionCount, knowledge: knowledgeCount ?? 0 };
  const item = (d: typeof SIMPLE_DESTINATIONS[number]) => {
    const on = d.active(view, placeholder, home);
    if (d.id === 'builds') return <BuildsItem key={d.id} d={d} on={on} onGo={() => go(d.go)} />;
    return <Box key={d.id} component="button" type="button" data-testid={`simple-nav-${d.id}`} aria-current={on ? 'page' : undefined} onClick={() => (d.id === 'search' ? d.go() : go(d.go))} sx={navSx(on)}>
      {d.icon}<span>{d.label}</span>
      {!!count[d.id] && <Box component="span" aria-label={`${count[d.id]} waiting`} sx={countSx}>{count[d.id] > 99 ? '99+' : count[d.id]}</Box>}
    </Box>;
  };
  return <Box component="aside" aria-label="Stroma" data-testid="simple-sidebar"
    sx={{ width: SIMPLE_SIDEBAR_WIDTH, maxWidth: '100%', height: '100%', flexShrink: 0, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: 'var(--side)', borderRight: '1px solid var(--line)', color: 'var(--ink)', fontFamily: 'var(--ui)', overflow: 'hidden' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1.75, pb: 1.25, pl: 2, pr: 1.5 }}>
      {/* the Stroma mark (the public site's and the mock's), not today's rail logo */}
      <Box component="img" data-testid="simple-brand-mark" src={`${process.env.PUBLIC_URL}/stroma-mark.svg`} alt="" sx={{ width: 24, height: 22, display: 'block' }} />
      <Box component="b" sx={{ fontWeight: 600, fontSize: 15, letterSpacing: '-.01em', flex: 1 }}>Stroma</Box>
      <Tooltip title={phone ? 'Close' : 'Hide the sidebar'}>
        <IconButton size="small" aria-label={phone ? 'Close the sidebar' : 'Hide the sidebar'}
          onClick={() => (phone ? useChatSidebar.getState().setDrawerOpen(false) : useSimpleSidebar.getState().setCollapsed(true))}
          sx={{ color: 'var(--ink-2)', borderRadius: '7px' }}><ViewSidebarOutlinedIcon sx={{ fontSize: 18, transform: 'scaleX(-1)' }} /></IconButton>
      </Tooltip>
    </Box>
    <Box component="button" type="button" data-testid="simple-new-chat" onClick={() => go(startNewQuestion)}
      sx={{ mx: 1.25, mb: 1, display: 'flex', alignItems: 'center', gap: 1.125, height: 36, px: 1.25, borderRadius: '9px', bgcolor: 'var(--bg)', border: '1px solid var(--line)', boxShadow: 'var(--sh-1)', font: 'inherit', fontWeight: 500, fontSize: 13.5, color: 'var(--ink)', cursor: 'pointer', textAlign: 'left', '&:hover': { borderColor: 'var(--ink-4)' }, ...FOCUS }}>
      <AddIcon sx={{ fontSize: 17 }} />New chat
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', px: 1.25, pb: 1.25, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box component="nav" aria-label="Main" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
        {SIMPLE_DESTINATIONS.filter((d) => d.group === 'main').map(item)}
      </Box>
      <Box>
        <Box sx={lblSx}>Build</Box>
        <Box component="nav" aria-label="Build" sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          {SIMPLE_DESTINATIONS.filter((d) => d.group === 'build' && (!d.shown || d.shown(features))).map(item)}
        </Box>
      </Box>
      <Box component="section" aria-label="Recents">
        <Box sx={lblSx}>Recents</Box>
        {recents.map((t) => <ThreadRow key={t.id} thread={t} onOpened={() => useSimpleSidebar.getState().showPlaceholder(null)} />)}
        {!recents.length && threadsPending && <Box data-testid="recents-loading" role="status" sx={{ px: 1.25, py: 0.5, color: 'var(--ink-3)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={12} thickness={5} sx={{ color: 'var(--ink-4)' }} />Loading your chats…</Box>}
        {!recents.length && !threadsPending && <Box sx={{ px: 1.25, py: 0.5, color: 'var(--ink-3)', fontSize: 13 }}>Your questions show here once you ask one.</Box>}
        {recents.length > 0 && <Box component="a" href="/history" onClick={(e: React.MouseEvent) => { e.preventDefault(); go(() => useVoyagerStore.getState().navigateToHistory()); }}
          sx={{ display: 'block', px: 1.25, pt: 0.75, fontSize: 12.5, color: 'var(--ink-3)', textDecoration: 'none', '&:hover': { color: 'var(--ink)' } }}>All chats →</Box>}
      </Box>
    </Box>
    {/* in the phone drawer the close Undo lives inside the modal's focus scope, as in today's sidebar */}
    {inDrawer && <CloseUndoToast inDrawer />}
    {inDrawer && <FinishedToastView inDrawer />}
    <Box sx={{ borderTop: '1px solid var(--line)', px: 1.25, py: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <UserMenu />
    </Box>
  </Box>;
}
